import React, { useRef, useState } from 'react';
import { Upload, X, File, Image, FileText, AlertCircle } from 'lucide-react';
import { apiUrl } from '../../config/api';

interface FileUploadProps {
  conversationId: string;
  onFileUploaded: (attachment: any) => void;
  disabled?: boolean;
}

interface UploadingFile {
  file: File;
  progress: number;
  error?: string;
}

export function FileUpload({ conversationId, onFileUploaded, disabled }: FileUploadProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadingFiles, setUploadingFiles] = useState<UploadingFile[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);

  const allowedTypes = [
    'image/jpeg',
    'image/png', 
    'image/gif',
    'image/webp',
    'application/pdf',
    'text/plain',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  ];

  const maxFileSize = 10 * 1024 * 1024; // 10MB

  const getFileIcon = (mimeType: string) => {
    if (mimeType.startsWith('image/')) {
      return <Image className="w-4 h-4" />;
    } else if (mimeType === 'application/pdf') {
      return <FileText className="w-4 h-4" />;
    } else {
      return <File className="w-4 h-4" />;
    }
  };

  const validateFile = (file: File): string | null => {
    if (!allowedTypes.includes(file.type)) {
      return 'File type not supported';
    }
    if (file.size > maxFileSize) {
      return 'File too large (max 10MB)';
    }
    return null;
  };

  const handleFileSelect = async (files: FileList | null) => {
    if (!files || disabled) return;

    const newFiles = Array.from(files);
    const validFiles: UploadingFile[] = [];

    // Validate files
    for (const file of newFiles) {
      const error = validateFile(file);
      if (error) {
        setUploadingFiles(prev => [...prev, { file, progress: 0, error }]);
        continue;
      }
      validFiles.push({ file, progress: 0 });
    }

    if (validFiles.length === 0) return;

    setUploadingFiles(prev => [...prev, ...validFiles]);

    // Upload files
    for (const uploadingFile of validFiles) {
      await uploadFile(uploadingFile.file);
    }
  };

  const uploadFile = async (file: File) => {
    try {
      // Get presigned URL
      const presignResponse = await fetch(apiUrl('/api/chat/attachments/presign'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          conversationId,
          filename: file.name,
          mime: file.type,
          size: file.size
        })
      });

      if (!presignResponse.ok) {
        throw new Error('Failed to get upload URL');
      }

      const { uploadUrl, filename } = await presignResponse.json();

      // Update progress
      setUploadingFiles(prev => prev.map(f => 
        f.file === file ? { ...f, progress: 50 } : f
      ));

      // Upload file
      const formData = new FormData();
      formData.append('file', file);

      const uploadResponse = await fetch(apiUrl(uploadUrl), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });

      if (!uploadResponse.ok) {
        throw new Error('Upload failed');
      }

      const uploadResult = await uploadResponse.json();

      // Update progress
      setUploadingFiles(prev => prev.map(f => 
        f.file === file ? { ...f, progress: 100 } : f
      ));

      // Call callback
      onFileUploaded({
        id: filename,
        name: file.name,
        url: uploadResult.url,
        mimeType: file.type,
        size: file.size
      });

      // Remove from uploading list after delay
      setTimeout(() => {
        setUploadingFiles(prev => prev.filter(f => f.file !== file));
      }, 1000);

    } catch (_error) {
      console.error('Upload error:', error);
      setUploadingFiles(prev => prev.map(f => 
        f.file === file ? { ...f, error: error instanceof Error ? error.message : 'Upload failed' } : f
      ));
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    handleFileSelect(e.dataTransfer.files);
  };

  const removeUploadingFile = (file: File) => {
    setUploadingFiles(prev => prev.filter(f => f.file !== file));
  };

  return (
    <div className="relative">
      {/* Upload Button */}
      <button
        onClick={() => fileInputRef.current?.click()}
        disabled={disabled}
        className="p-2 hover:bg-gray-100 rounded-full transition-colors disabled:opacity-50"
        title="Attach file"
      >
        <Upload className="w-5 h-5 text-gray-500" />
      </button>

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={allowedTypes.join(',')}
        onChange={(e) => handleFileSelect(e.target.files)}
        className="hidden"
      />

      {/* Drag and Drop Overlay */}
      {isDragOver && (
        <div
          className="fixed inset-0 bg-blue-500 bg-opacity-20 z-50 flex items-center justify-center"
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <div className="bg-white rounded-lg p-8 shadow-lg">
            <Upload className="w-12 h-12 text-blue-500 mx-auto mb-4" />
            <p className="text-lg font-medium text-gray-900">Drop files to upload</p>
            <p className="text-sm text-gray-600">Images, PDFs, and documents up to 10MB</p>
          </div>
        </div>
      )}

      {/* Uploading Files List */}
      {uploadingFiles.length > 0 && (
        <div className="absolute bottom-full right-0 mb-2 bg-white border border-gray-200 rounded-lg shadow-lg p-2 min-w-64 z-10">
          {uploadingFiles.map((uploadingFile, index) => (
            <div key={index} className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded">
              {getFileIcon(uploadingFile.file.type)}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {uploadingFile.file.name}
                </p>
                {uploadingFile.error ? (
                  <div className="flex items-center space-x-1 text-red-600">
                    <AlertCircle className="w-3 h-3" />
                    <p className="text-xs">{uploadingFile.error}</p>
                  </div>
                ) : (
                  <div className="flex items-center space-x-2">
                    <div className="flex-1 bg-gray-200 rounded-full h-1">
                      <div
                        className="bg-blue-500 h-1 rounded-full transition-all duration-300"
                        style={{ width: `${uploadingFile.progress}%` }}
                      />
                    </div>
                    <span className="text-xs text-gray-500">
                      {uploadingFile.progress}%
                    </span>
                  </div>
                )}
              </div>
              <button
                onClick={() => removeUploadingFile(uploadingFile.file)}
                className="p-1 hover:bg-gray-200 rounded-full transition-colors"
              >
                <X className="w-3 h-3 text-gray-500" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
